import {inject, Injectable, signal} from '@angular/core';
import {Router} from '@angular/router';
import {firstValueFrom} from 'rxjs';
import {NzModalService} from 'ng-zorro-antd/modal';
import {TranslocoService} from '@jsverse/transloco';
import {StorageService} from '../../../services/storage.service';
import {
  SelectOrgComponent,
  type SelectOrgDialogData,
  type SelectOrgDialogResult,
} from './select-org.component';

/** Provided by AppComponent so it shares the component-level NzModalModule. */
@Injectable()
export class SelectOrgService {
  private readonly modal = inject(NzModalService);
  private readonly storageService = inject(StorageService);
  private readonly transloco = inject(TranslocoService);
  private readonly router = inject(Router);

  /** Current org display name for shell chrome. */
  readonly orgName = signal('');
  /** Bumped after org changes so base-layout remounts outlet content. */
  private readonly _orgEpoch = signal(0);
  readonly orgEpoch = this._orgEpoch.asReadonly();

  private openPromise: Promise<boolean> | null = null;

  async hydrate(): Promise<void> {
    this.orgName.set(String((await this.storageService.lfGet('orgName')) ?? ''));
  }

  async hasOrg(): Promise<boolean> {
    return Boolean(await this.storageService.lfGet('orgId'));
  }

  /** Lock background until an organization is selected. No-op when already set. */
  async ensureSelected(): Promise<boolean> {
    await this.hydrate();
    if (await this.hasOrg()) {
      return true;
    }
    return this.open({required: true});
  }

  /** Header “switch organization”; cancelable when an org is already selected. */
  async switchOrg(): Promise<boolean> {
    const required = !(await this.hasOrg());
    return this.open({required});
  }

  private async open(options: {required: boolean}): Promise<boolean> {
    if (this.openPromise) {
      return this.openPromise;
    }

    this.openPromise = this.openOnce(options).finally(() => {
      this.openPromise = null;
    });
    return this.openPromise;
  }

  private async openOnce(options: {required: boolean}): Promise<boolean> {
    const ref = this.modal.create<
      SelectOrgComponent,
      SelectOrgDialogData,
      SelectOrgDialogResult | null
    >({
      nzTitle: this.transloco.translate('account.select_org.title'),
      nzContent: SelectOrgComponent,
      nzData: {required: options.required},
      nzFooter: null,
      nzClosable: !options.required,
      nzMaskClosable: !options.required,
      nzKeyboard: !options.required,
      nzCentered: true,
      nzWidth: 420,
    });

    const result = (await firstValueFrom(ref.afterClose)) ?? null;
    if (!result) {
      await this.hydrate();
      return false;
    }

    this.orgName.set(result.orgName);
    this._orgEpoch.update((n) => n + 1);
    await this.router.navigateByUrl('/site/list');
    return true;
  }
}
